import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["text"]
    static values = {
        autoShowOnClick: { type: Boolean, default: true }
    }

    initialize() {
        // Bind once so the same references can be removed on disconnect
        this.showLoading = this.showLoading.bind(this)
        this.restoreButton = this.restoreButton.bind(this)
    }

    connect() {
        // Store the original button text when controller connects
        if (this.hasTextTarget) {
            this.originalText = this.textTarget.textContent
        } else {
            this.originalText = this.element.textContent.trim()
        }

        // Add submit event listener to the form
        if (this.element.tagName === "BUTTON" && this.element.type === "submit") {
            // Keep a reference: by disconnect time the button may already be
            // detached from its form
            this.form = this.element.form || this.element.closest("form")
            if (this.form) {
                this.form.addEventListener("submit", this.showLoading)
            }
        } else {
            if (this.autoShowOnClickValue) {
                this.element.addEventListener("click", this.showLoading)
            }
        }

        // Listen for a custom event to stop the loading state
        this.element.addEventListener("loading-button:stop", this.restoreButton);
    }

    disconnect() {
        // Clean up event listeners
        if (this.form) {
            this.form.removeEventListener("submit", this.showLoading)
            this.form = null
        }
        this.element.removeEventListener("click", this.showLoading)
        this.element.removeEventListener("loading-button:stop", this.restoreButton)
        this.stopListeningForTurboEvents()

        // Drop any in-progress loading state
        if (this.animationInterval) {
            this.restoreButton()
        }
    }

    showLoading(event = null) {
        // A form with several submit buttons only loads the one that submitted it
        if (event?.submitter && event.submitter !== this.element) return

        // Don't show loading state if the form is invalid (only if event is present and from a form submission)
        if (event && event.target && event.target.tagName === "FORM" && !event.target.checkValidity()) {
            return
        }

        // Get the element to update (either the text target or the button itself)
        const textElement = this.hasTextTarget ? this.textTarget : this.element

        // Store original text if not already stored (e.g. if showLoading is called programmatically before connect finishes or if element text changes)
        if (!this.originalText || (this.hasTextTarget && this.originalText !== this.textTarget.textContent) || (!this.hasTextTarget && this.originalText !== this.element.textContent.trim())) {
            if (this.hasTextTarget) {
                this.originalText = this.textTarget.textContent;
            } else {
                this.originalText = this.element.textContent.trim();
            }
        }

        // If already loading, don't do anything
        if (this.animationInterval) {
            return;
        }

        // Freeze the button's current box so swapping the label for dots can't
        // resize it, regardless of how the individual button is styled
        this.lockDimensions()

        // Create the loading dots container. It stays inline so it sits on the
        // button's existing line box and inherits its line-height
        const dotsContainer = document.createElement('div')
        dotsContainer.classList.add('loading-dots-container')
        dotsContainer.style.display = 'inline-flex'
        dotsContainer.style.justifyContent = 'center'
        dotsContainer.style.alignItems = 'center'
        dotsContainer.style.verticalAlign = 'middle'
        dotsContainer.style.gap = '6px'

        // Create the three dots
        for (let i = 0; i < 3; i++) {
            const dot = document.createElement('div')
            dot.classList.add('loading-dot')
            dot.dataset.index = i
            dot.style.width = '6px'
            dot.style.height = '6px'
            dot.style.borderRadius = '50%'
            dot.style.backgroundColor = 'currentColor'
            dot.style.opacity = '1'
            dot.style.transition = 'transform 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)'
            dotsContainer.appendChild(dot)
        }

        // Clear the text element and append the dots container
        textElement.textContent = ''
        textElement.appendChild(dotsContainer)

        // Set up the pulsing animation
        let currentDotIndex = 0
        const dots = dotsContainer.querySelectorAll('.loading-dot')

        // Reset all dots to normal size
        const resetDots = () => {
            dots.forEach(dot => {
                dot.style.transform = 'scale(1)'
            })
        }

        // Initial state
        resetDots()

        // Start the animation
        this.animationInterval = setInterval(() => {
            resetDots()
            if (dots[currentDotIndex]) { // Ensure dot exists
                dots[currentDotIndex].style.transform = 'scale(1.5)'
            }
            currentDotIndex = (currentDotIndex + 1) % 3
        }, 300)

        // Listen for turbo events to restore the button
        document.addEventListener("turbo:submit-end", this.restoreButton, { once: true })
        document.addEventListener("turbo:frame-load", this.restoreButton, { once: true })
    }

    stopListeningForTurboEvents() {
        document.removeEventListener("turbo:submit-end", this.restoreButton)
        document.removeEventListener("turbo:frame-load", this.restoreButton)
    }

    restoreButton() {
        this.stopListeningForTurboEvents()

        // Clear animation interval
        if (this.animationInterval) {
            clearInterval(this.animationInterval)
            this.animationInterval = null
        }

        // Restore original text
        const textElement = this.hasTextTarget ? this.textTarget : this.element
        textElement.innerHTML = '' // Clear dots
        textElement.textContent = this.originalText

        this.unlockDimensions()
    }

    // Pin the button to the size it has right now, so the loading state can
    // never make it grow or shrink
    lockDimensions() {
        // min-width/min-height don't apply to inline elements, and an inline
        // element's line box is already held open by its line-height
        if (getComputedStyle(this.element).display === 'inline') {
            return
        }

        const { width, height } = this.element.getBoundingClientRect()
        if (!width || !height) {
            return
        }

        this.lockedStyles = {
            minWidth: this.element.style.minWidth,
            minHeight: this.element.style.minHeight
        }
        this.element.style.minWidth = `${width}px`
        this.element.style.minHeight = `${height}px`
    }

    unlockDimensions() {
        if (!this.lockedStyles) {
            return
        }

        this.element.style.minWidth = this.lockedStyles.minWidth
        this.element.style.minHeight = this.lockedStyles.minHeight
        this.lockedStyles = null
    }
}
